import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook to track user activity (page views, actions, session heartbeat)
 * Automatically tracks page navigation and provides methods to log custom actions
 */
export const useActivityTracker = () => {
  const { token, user } = useAuth();
  const location = useLocation();
  const lastPathRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);

  // Log activity to the server
  const logActivity = useCallback(async (activityData) => {
    if (!token) return;
    
    try {
      await fetch(`${API_URL}/api/activity/log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(activityData)
      });
    } catch (error) {
      // Silently fail - don't disrupt user experience
      console.debug('Activity tracking error:', error);
    }
  }, [token]);

  // Log page view
  const logPageView = useCallback((page) => {
    logActivity({
      action_type: 'page_view',
      page: page
    });
  }, [logActivity]);

  // Log custom action (exported for use in components)
  const logAction = useCallback((actionDetail, page = null, metadata = null) => {
    logActivity({
      action_type: 'action',
      page: page || location.pathname,
      action_detail: actionDetail,
      metadata: metadata
    });
  }, [logActivity, location.pathname]);

  // Send heartbeat to keep session alive
  const sendHeartbeat = useCallback(async () => {
    if (!token) return;
    
    try {
      await fetch(`${API_URL}/api/activity/heartbeat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_page: location.pathname
        })
      });
    } catch (error) {
      console.debug('Heartbeat error:', error);
    }
  }, [token, location.pathname]);

  // Log login when user authenticates
  const logLogin = useCallback(async () => {
    if (!token) return;
    
    try {
      await fetch(`${API_URL}/api/activity/login`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (error) {
      console.debug('Login tracking error:', error);
    }
  }, [token]);

  // Log logout when user logs out
  const logLogout = useCallback(async () => {
    if (!token) return;
    
    try {
      await fetch(`${API_URL}/api/activity/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (error) {
      console.debug('Logout tracking error:', error);
    }
  }, [token]);

  // Track page navigation
  useEffect(() => {
    if (!token || !user) return;
    
    const currentPath = location.pathname;
    
    // Only log if path changed
    if (currentPath !== lastPathRef.current) {
      lastPathRef.current = currentPath;
      logPageView(currentPath);
    }
  }, [location.pathname, token, user, logPageView]);

  // Setup heartbeat interval (every 60 seconds)
  useEffect(() => {
    if (!token || !user) return;

    // Initial login tracking
    logLogin();

    // Setup heartbeat
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, 60000);

    // Cleanup on unmount or logout
    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [token, user, logLogin, sendHeartbeat]);

  // Handle page unload (logout tracking)
  useEffect(() => {
    if (!token) return;

    const handleBeforeUnload = () => {
      // Use sendBeacon for reliable delivery on page unload
      if (navigator.sendBeacon) {
        navigator.sendBeacon(
          `${API_URL}/api/activity/logout`,
          new Blob([JSON.stringify({})], { type: 'application/json' })
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [token]);

  return {
    logAction,
    logPageView,
    logLogin,
    logLogout
  };
};

export default useActivityTracker;
